import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { EditingCell, EmployeeData } from "../EmployeeDataDrawer";
import { validateCellValue } from "./useEmployeeValidation";

interface UseEmployeeEditingProps {
  localPersonnelData: EmployeeData[];
  setLocalPersonnelData: React.Dispatch<React.SetStateAction<EmployeeData[]>>;
  setValidationError: React.Dispatch<React.SetStateAction<string>>;
}

interface UseEmployeeEditingReturn {
  editingCell: EditingCell | null;
  editValue: string;
  setEditValue: React.Dispatch<React.SetStateAction<string>>;
  handleCellClick: (
    rowIndex: number,
    colKey: string,
    value: string | number | undefined,
    rowKey: string,
  ) => void;
  handleSaveCell: () => void;
  handleCancelEdit: () => void;
  handleKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  resetEditing: () => void;
}

export const useEmployeeEditing = ({
  localPersonnelData,
  setLocalPersonnelData,
  setValidationError,
}: UseEmployeeEditingProps): UseEmployeeEditingReturn => {
  const { t } = useTranslation();
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>("");

  const handleCellClick = useCallback(
    (
      rowIndex: number,
      colKey: string,
      value: string | number | undefined,
      rowKey: string,
    ): void => {
      // Key field cannot be edited
      if (colKey === "key") {
        setValidationError(t("paygap.employeeData.validation.keyNotEditable"));

return;
      }

      setEditingCell({ rowIndex, colKey, rowKey });
      setEditValue(value !== undefined && value !== null ? String(value) : "");
      setValidationError("");
    },
    [setValidationError, t],
  );

  const handleSaveCell = useCallback((): void => {
    if (!editingCell) return;

    const validation = validateCellValue(t, editingCell.colKey, editValue);

    if (!validation.isValid) {
      setValidationError(validation.errorMessage);

return;
    }

    // Find the row by key and update the value
    setLocalPersonnelData((prev) =>
      prev.map((employee) => {
        if (String(employee.key) === editingCell.rowKey) {
          return { ...employee, [editingCell.colKey]: editValue };
        }

return employee;
      }),
    );

    setEditingCell(null);
    setEditValue("");
    setValidationError("");
  }, [editingCell, editValue, setLocalPersonnelData, setValidationError, t]);

  const handleCancelEdit = useCallback((): void => {
    setEditingCell(null);
    setEditValue("");
    setValidationError("");
  }, [setValidationError]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "Enter") {
        handleSaveCell();
      } else if (event.key === "Escape") {
        handleCancelEdit();
      }
    },
    [handleSaveCell, handleCancelEdit],
  );

  const resetEditing = useCallback((): void => {
    setEditingCell(null);
    setEditValue("");
  }, []);

  return {
    editingCell,
    editValue,
    setEditValue,
    handleCellClick,
    handleSaveCell,
    handleCancelEdit,
    handleKeyPress,
    resetEditing,
  };
};
